import ReaderBottomDock from '@components/shared/ReaderBottomDock/ReaderBottomDock'
import { PageType } from '@app-types/PageType'
import QuickActions from './QuickActions'
import SwipeIndicator from './SwipeIndicator'
import SettingsModal from '@components/shared/SettingsModal/SettingsModal'
import StopWatch from '@components/shared/Stopwatch/Stopwatch'
import useAutoScroll from '@hooks/useAutoScroll'
import { useFocusMode } from '@hooks/useFocusMode'
import { useGesture } from '@hooks/useGesture'
import usePagination from '@hooks/usePage'
import useReadingProgress from '@hooks/useReadingProgress'
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FC,
  type FormEvent,
  type ReactNode,
} from 'react'
import toast from 'react-hot-toast'
import {
  FaAnglesDown,
  FaBookmark,
  FaClock,
  FaPause,
  FaRegBookmark,
} from 'react-icons/fa6'
import FocusModeButton from './FocusModeButton'
import FocusModeOverlay from './FocusModeOverlay'
import NoSleepManager from './NoSleepManager'
import ToolbarButton from './ToolbarButton'

type PaginationAndSettingsRowProps = {
  type: PageType
  max: number
  min?: number
  basePath?: string
  mobileReader?: boolean
  /** Pauses scrolling while the native reader is fetching the next page. */
  autoScrollReady?: boolean
  /** Render function for the content shown in focus mode. Uses a function to avoid mounting content when not in focus mode. */
  renderFocusContent?: () => ReactNode
  /** Reuses the dua dock while preserving the existing reader state and gestures. */
  bottomDock?: {
    audioPanel: ReactNode
    isAudioPanelOpen: boolean
    isAudioPlaying: boolean
    onToggleAudioPanel: () => void
  }
}

type SwipeState = {
  direction: 'left' | 'right' | null
  progress: number
  isActive: boolean
  isTriggered: boolean
}

export const PaginationAndSettingsRow: FC<PaginationAndSettingsRowProps> = ({
  type,
  max,
  min = 1,
  basePath = '',
  mobileReader = false,
  autoScrollReady = true,
  renderFocusContent,
  bottomDock,
}) => {
  const { isFocusMode, toggleFocusMode } = useFocusMode()
  const focusScrollContainerRef = useRef<HTMLDivElement>(null)
  const { nextPage, page, previousPage, goToPage } = usePagination({
    path: type,
    max,
    min,
    basePath,
    mobileReader,
  })

  // Auto Scroll Integration
  const { isScrolling, isPending, toggleScroll, duration, setDuration } = useAutoScroll(
    type,
    focusScrollContainerRef,
    isFocusMode,
    autoScrollReady,
  )
  const [swipeState, setSwipeState] = useState<SwipeState>({
    direction: null,
    progress: 0,
    isActive: false,
    isTriggered: false,
  })

  const handleSwipeEnd = useCallback(
    (direction: 'left' | 'right') => {
      setSwipeState((prev) => ({ ...prev, isTriggered: true, progress: 1 }))

      if (direction === 'left') {
        previousPage()
      } else {
        nextPage()
      }

      // Delay reset to allow "shoot-through" animation to complete
      setTimeout(() => {
        setSwipeState({
          direction: null,
          progress: 0,
          isActive: false,
          isTriggered: false,
        })
      }, 500)
    },
    [nextPage, previousPage],
  )

  useGesture({
    onSwipeLeft: () => handleSwipeEnd('left'),
    onSwipeRight: () => handleSwipeEnd('right'),
    onSwipeMove: (direction, progress) => {
      setSwipeState({ direction, progress, isActive: true, isTriggered: false })
    },
    onSwipeCancel: () => {
      setSwipeState({ direction: null, progress: 0, isActive: false, isTriggered: false })
    },
  })
  const [pageForInput, setPageForInput] = useState(page)

  const onChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setPageForInput(Number(event.target.value))
  }

  const notify = useCallback((msg = 'Kalınan yer kaydedildi!') => {
    toast.custom(
      (t) => (
        <div
          className="flex justify-between items-center bg-slate-600 p-2 rounded-lg text-white"
          onClick={() => toast.dismiss(t.id)}
        >
          🔖 {msg}{' '}
        </div>
      ),
      {
        duration: 1500,
        position: 'top-center',
      },
    )
  }, [])

  const { userData, updateUserData } = useReadingProgress(type, page, notify)

  const onPageChangeSubmit = (event: FormEvent) => {
    event.preventDefault()
    goToPage(pageForInput)
  }

  useEffect(() => {
    setPageForInput(page)
  }, [page])

  const getReadPageCount = () => {
    const isLastPage = Number(page) === max
    const lastRead = userData[type] as number
    const readPageCount = isLastPage
      ? Number(page) - lastRead + 1
      : Number(page) - lastRead
    return readPageCount
  }

  const handleBookmark = async () => {
    // if the page is already marked, unmark it
    if (Number(userData[type]) === Number(page)) {
      updateUserData(type, 0)
      notify('Kalınan yer kaldırıldı!')
      return
    }

    updateUserData(type, Number(page))
    notify()
  }

  const isMarked = Number(userData[type]) === Number(page)

  const quickActionsRenderData = {
    isScrolling,
    isPending,
    toggleScroll,
    basePath,
    type,
    isMarked,
    handleBookmark,
    getReadPageCount,
    page,
  }

  const toolbar = (
    <div className="flex justify-between items-center mb-2 sticky top-0 z-10 py-3 -mx-3 sm:-mx-8 sm:px-8 px-2 bg-main/90 dark:bg-primary-dark-500/90 backdrop-blur-md border-b border-main/50">
      <div className="flex items-center justify-start w-[33%] gap-1">
        <StopWatch
          className="text-[14px] font-bold text-muted bg-surface-subtle px-2 py-1.5 rounded-xl border border-main shrink-0"
          width={75}
          getTime={() => {}}
        />
        <ToolbarButton
          onClick={toggleScroll}
          ariaLabel={
            isScrolling
              ? 'Otomatik kaydırmayı durdur'
              : isPending
                ? 'Kaydırma başlıyor...'
                : 'Otomatik kaydırmayı başlat'
          }
          className={
            isScrolling
              ? 'bg-surface-strong text-brand-secondary ring-1 ring-brand-secondary/30'
              : 'text-main hover:bg-surface-subtle border border-transparent'
          }
        >
          {isScrolling ? (
            <FaPause />
          ) : isPending ? (
            <FaClock className="animate-pulse text-brand-secondary" />
          ) : (
            <FaAnglesDown />
          )}
        </ToolbarButton>
      </div>

      <div className="flex justify-center items-center w-[34%]">
        <form className="relative" onSubmit={onPageChangeSubmit}>
          <input
            className="bg-main ring-1 ring-black/5 dark:ring-white/10 rounded-xl w-14 sm:w-16 h-8 font-bold text-sm text-center outline-none shadow-sm focus:ring-2 focus:ring-brand-secondary/50 transition-all"
            type="number"
            name="page"
            id="top-pagination-page"
            max={max}
            min={min}
            value={pageForInput}
            onChange={onChangeHandler}
            aria-label="Sayfa numarası"
          />
        </form>
      </div>

      <div className="flex justify-end items-center w-[33%] gap-1">
        <ToolbarButton
          onClick={handleBookmark}
          id="bookmark"
          ariaLabel={isMarked ? 'Yer işaretini kaldır' : 'Yer işareti ekle'}
          className={
            isMarked
              ? 'text-emerald-600 bg-emerald-50 dark:bg-emerald-500/10 ring-1 ring-emerald-500/30'
              : 'text-main hover:bg-surface-subtle border border-transparent'
          }
        >
          {isMarked ? <FaBookmark /> : <FaRegBookmark />}
        </ToolbarButton>
        <FocusModeButton isFocusMode={isFocusMode} onToggle={toggleFocusMode} />

        <SettingsModal
          type={type}
          autoScrollDuration={duration}
          setAutoScrollDuration={setDuration}
          quickActions={<QuickActions {...quickActionsRenderData} />}
        />
      </div>
    </div>
  )

  return (
    <>
      <NoSleepManager />
      {bottomDock ? (
        <ReaderBottomDock
          type={type}
          page={page}
          minPage={min}
          maxPage={max}
          onGoToPage={goToPage}
          isScrolling={isScrolling}
          isPending={isPending}
          onToggleScroll={toggleScroll}
          isPageMarked={isMarked}
          onToggleBookmark={handleBookmark}
          isFocusMode={isFocusMode}
          onToggleFocusMode={toggleFocusMode}
          autoScrollDuration={duration}
          setAutoScrollDuration={setDuration}
          quickActions={<QuickActions {...quickActionsRenderData} />}
          {...bottomDock}
        />
      ) : (
        toolbar
      )}
      {isFocusMode && (
        <FocusModeOverlay
          onExit={toggleFocusMode}
          scrollContainerRef={focusScrollContainerRef}
        >
          {renderFocusContent?.()}
          {bottomDock && (
            <div
              aria-hidden="true"
              className={bottomDock.isAudioPanelOpen ? 'h-56' : 'h-20'}
            />
          )}
        </FocusModeOverlay>
      )}

      <SwipeIndicator swipeState={swipeState} />
    </>
  )
}

export default PaginationAndSettingsRow
